import {
  BOLT_GRADES,
  BOLT_SIZES,
  MATERIALS,
  MEMBER_TYPES,
  NOMINAL_SPECS,
  RFQ_CATEGORIES,
  RFQ_PRICE_MODES,
  SECTION_TYPES,
  TREATMENTS,
  TRUCK_TYPES,
  WELD_GRADES,
} from "@/lib/rfq/constants";
import {
  Button,
  Checkbox,
  NumberInput,
  SegmentedControl,
  Select,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconBoxMultiple,
  IconDownload,
  IconInfoCircle,
  IconListNumbers,
  IconLock,
  IconPlus,
  IconStack2,
  IconTrash,
} from "@tabler/icons-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import type { RfqLine, RfqPriceMode, RfqRole } from "@/lib/rfq/types";
import { fmtWeight, resolveWeight } from "@/lib/rfq/calc";

import RfqAddLineModal from "./RfqAddLineModal";
import RfqImportModal from "./RfqImportModal";
import type { RfqLineInput, RfqProductOption } from "@/lib/rfq/api";
import { RfqSection } from "./RfqSection";
import { useRfqLabels } from "@/lib/rfq/labels";

function Th({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={`whitespace-nowrap border-b border-gray-200 px-3 py-2.5 font-medium ${className}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={`border-b border-gray-100 px-3 py-2 align-top ${className}`}>
      {children}
    </td>
  );
}

function MiniSelect({
  label,
  options,
  wide,
}: {
  label: string;
  options: string[];
  wide?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] text-gray-500">{label}</span>
      <Select
        size="xs"
        disabled
        className={wide ? "w-[150px]" : "w-[96px]"}
        data={options}
        value={null}
        placeholder="—"
        aria-label={label}
      />
    </div>
  );
}

function MiniNumber({ label }: { label: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] text-gray-500">{label}</span>
      <NumberInput
        size="xs"
        disabled
        className="w-[84px]"
        hideControls
        value=""
        aria-label={label}
      />
    </div>
  );
}

function CategoryFields({ line }: { line: RfqLine }) {
  const { t } = useRfqLabels();
  const hint =
    t("rfq.detail.categoryFieldsDisabled") ||
    "後端未提供類別明細欄位，暫時無法編輯";

  const body = (() => {
    switch (line.category) {
      case "material":
        return (
          <>
            <MiniSelect
              label={t("rfq.detail.fieldMaterial") || "材質"}
              options={MATERIALS}
            />
            <MiniSelect
              label={t("rfq.detail.fieldSectionType") || "斷面型別"}
              options={SECTION_TYPES}
            />
            <MiniSelect
              label={t("rfq.detail.fieldNominalSpec") || "標稱規格"}
              options={NOMINAL_SPECS}
              wide
            />
            <MiniNumber label={t("rfq.detail.fieldLength") || "長度(mm)"} />
            <MiniNumber label={t("rfq.detail.fieldCount") || "支數"} />
          </>
        );
      case "fabrication":
        return (
          <>
            <MiniSelect
              label={t("rfq.detail.fieldMemberType") || "構件類型"}
              options={MEMBER_TYPES}
            />
            <MiniSelect
              label={t("rfq.detail.fieldWeldGrade") || "銲道等級"}
              options={WELD_GRADES}
            />
            <MiniNumber label={t("rfq.detail.fieldUtRatio") || "UT 比例(%)"} />
          </>
        );
      case "surface":
        return (
          <MiniSelect
            label={t("rfq.detail.fieldTreatment") || "處理方式"}
            options={TREATMENTS}
            wide
          />
        );
      case "bolt":
        return (
          <>
            <MiniSelect
              label={t("rfq.detail.fieldBoltGrade") || "等級"}
              options={BOLT_GRADES}
            />
            <MiniSelect
              label={t("rfq.detail.fieldBoltSize") || "尺寸"}
              options={BOLT_SIZES}
            />
          </>
        );
      case "transport":
        return (
          <>
            <MiniSelect
              label={t("rfq.detail.fieldOrigin") || "起點"}
              options={[]}
              wide
            />
            <MiniSelect
              label={t("rfq.detail.fieldDestination") || "迄點"}
              options={[]}
              wide
            />
            <MiniSelect
              label={t("rfq.detail.fieldTruckType") || "車型"}
              options={TRUCK_TYPES}
            />
          </>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 pt-1.5 text-[11px] text-gray-500">
            <IconInfoCircle size={12} />
            {t("rfq.detail.noExtraField") || "此類別無額外欄位"}
          </span>
        );
    }
  })();

  return (
    <Tooltip label={hint} withArrow multiline w={240}>
      <div className="flex flex-wrap items-end gap-2 opacity-60">{body}</div>
    </Tooltip>
  );
}

function LineRow({
  line,
  selected,
  onToggle,
  onUpdate,
  canSeeTarget,
  editable,
  tint,
}: {
  line: RfqLine;
  selected: boolean;
  onToggle: () => void;
  onUpdate: (patch: Partial<RfqLine>) => void;
  canSeeTarget: boolean;
  editable: boolean;
  tint?: boolean;
}) {
  const { t, category, priceMode, calc } = useRfqLabels();
  const weight = resolveWeight(line, calc);
  const [qty, setQty] = useState<number | string>(line.qty);
  const [target, setTarget] = useState<number | string>(line.targetPrice ?? "");
  const [note, setNote] = useState(line.note ?? "");

  useEffect(() => setQty(line.qty), [line.qty]);
  useEffect(() => setTarget(line.targetPrice ?? ""), [line.targetPrice]);
  useEffect(() => setNote(line.note ?? ""), [line.note]);

  const rowBg = selected
    ? "bg-blue-50/60"
    : tint
      ? "bg-gray-50/60"
      : "bg-white";

  return (
    <tr className={rowBg}>
      <Td className={`sticky left-0 z-10 ${rowBg}`}>
        <div className="flex items-center gap-2">
          <Checkbox
            size="xs"
            checked={selected}
            disabled={!editable}
            onChange={onToggle}
            aria-label={`${t("rfq.detail.colSeq") || "項次"} ${line.seq}`}
          />
          <span className="text-gray-500">{line.seq}</span>
        </div>
      </Td>
      <Td
        className={`sticky left-[76px] z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] ${rowBg}`}
      >
        <span className="pt-1.5 font-medium text-gray-700">{line.name}</span>
      </Td>
      <Td>
        <Select
          size="xs"
          disabled
          data={RFQ_CATEGORIES.map((value) => ({
            value,
            label: category(value),
          }))}
          value={line.category}
          aria-label={t("rfq.detail.colCategory") || "類別"}
        />
      </Td>
      <Td>
        <span className="inline-flex items-center pt-1.5 text-xs text-gray-600">
          {line.spec || "—"}
        </span>
      </Td>
      <Td>
        <NumberInput
          size="xs"
          hideControls
          min={0}
          disabled={!editable}
          value={qty}
          onChange={(value) => setQty(value)}
          onBlur={() => {
            const next = Number(qty) || 0;
            if (next !== line.qty) onUpdate({ qty: next, count: next });
          }}
          aria-label={t("rfq.detail.colQty") || "詢價數量"}
        />
      </Td>
      <Td>
        <span className="inline-flex items-center pt-1.5 text-xs text-gray-600">
          {line.unit || "—"}
        </span>
      </Td>
      <Td>
        <Select
          size="xs"
          disabled={!editable}
          data={RFQ_PRICE_MODES.map((value) => ({
            value,
            label: priceMode(value),
          }))}
          value={line.priceMode}
          onChange={(value) =>
            value ? onUpdate({ priceMode: value as RfqPriceMode }) : undefined
          }
          aria-label={t("rfq.detail.colPriceMode") || "比價模式"}
        />
      </Td>
      {/* <Td>
        <CategoryFields line={line} />
      </Td> */}
      <Td>
        {line.priceMode === "weight" ? (
          <div className="flex flex-col gap-1">
            <span className="block text-sm tabular-nums text-gray-700">
              {weight ? fmtWeight(weight.kg) : "—"}
            </span>
            {weight ? (
              <span className="block text-[11px] leading-tight text-gray-500">
                {weight.formula}
              </span>
            ) : (
              <span className="text-[11px] text-red-500">
                {t("rfq.detail.noWeight") || "缺規格，無法試算"}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400">
            {t("rfq.detail.unitPriced") || "—（單位計價）"}
          </span>
        )}
      </Td>
      <Td>
        <TextInput
          size="xs"
          type="date"
          disabled={!editable}
          value={line.dueDate ?? ""}
          onChange={(event) => onUpdate({ dueDate: event.currentTarget.value })}
          aria-label={t("rfq.detail.colDueDate") || "需求交期"}
        />
      </Td>
      <Td>
        <div className="flex justify-center pt-1.5">
          <Checkbox
            size="xs"
            disabled={!editable}
            checked={!!line.mtc}
            onChange={(event) => onUpdate({ mtc: event.currentTarget.checked })}
            aria-label={t("rfq.detail.colMtc") || "材證"}
          />
        </div>
      </Td>
      {canSeeTarget ? (
        <Td>
          <NumberInput
            size="xs"
            hideControls
            min={0}
            disabled={!editable}
            value={target}
            placeholder="—"
            onChange={(value) => setTarget(value ?? "")}
            onBlur={() => {
              const next = target === "" ? undefined : Number(target);
              if (next !== line.targetPrice) onUpdate({ targetPrice: next });
            }}
            aria-label={t("rfq.detail.colTargetPrice") || "目標單價"}
          />
        </Td>
      ) : null}
      <Td>
        <TextInput
          size="xs"
          disabled={!editable}
          value={note}
          placeholder="—"
          onChange={(event) => setNote(event.currentTarget.value)}
          onBlur={() => {
            if (note !== (line.note ?? "")) onUpdate({ note });
          }}
          aria-label={t("rfq.detail.colNote") || "備註"}
        />
      </Td>
    </tr>
  );
}

function RfqDetailTab({
  lines,
  role,
  editable,
  products,
  onCreateLines,
  onUpdateLine,
  onDeleteLines,
}: {
  lines: RfqLine[];
  role: RfqRole;
  editable: boolean;
  products: RfqProductOption[];
  onCreateLines: (
    inputs: Omit<RfqLineInput, "seq">[],
    sourceOrderIds?: string[],
  ) => Promise<void> | void;
  onUpdateLine: (id: string, patch: Partial<RfqLine>) => Promise<void> | void;
  onDeleteLines: (ids: string[]) => Promise<void> | void;
}) {
  const { t, category, calc } = useRfqLabels();
  const [view, setView] = useState<"seq" | "group">("seq");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const canSeeTarget = role === "manager";

  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const deleteSelected = async () => {
    await onDeleteLines(Array.from(selected));
    setSelected(new Set());
  };

  const groups = useMemo(() => {
    if (view === "seq") return null;
    const map = new Map<string, RfqLine[]>();
    for (const line of lines) {
      const key = `${category(line.category)} / ${line.spec || line.name}`;
      if (!map.has(key)) map.set(key, []);
      (map.get(key) as RfqLine[]).push(line);
    }
    return Array.from(map.entries());
  }, [lines, view, category]);

  const columnCount = canSeeTarget ? 12 : 11;

  const modals = (
    <>
      <RfqAddLineModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        products={products}
        onAdd={onCreateLines}
        canSeeTarget={canSeeTarget}
      />
      <RfqImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        products={products}
        onImport={onCreateLines}
      />
    </>
  );

  if (lines.length === 0) {
    return (
      <>
        <RfqSection
          icon={IconBoxMultiple}
          title={t("rfq.detail.sectionTitle") || "詢價明細"}
        >
          <div className="flex flex-col items-center gap-4 py-14">
            <p className="text-sm text-gray-500">
              {t("rfq.detail.empty") || "尚無明細，先從採購單帶入項目。"}
            </p>
            <div className="flex gap-2">
              <Button
                variant="default"
                radius="md"
                disabled={!editable}
                leftSection={<IconPlus size={16} />}
                onClick={() => setAddOpen(true)}
              >
                {t("rfq.detail.addManual") || "手動新增"}
              </Button>
              <Button
                color="blue"
                radius="md"
                disabled={!editable}
                leftSection={<IconDownload size={16} />}
                onClick={() => setImportOpen(true)}
              >
                {t("rfq.detail.importFromPo") || "從採購單帶入"}
              </Button>
            </div>
          </div>
        </RfqSection>
        {modals}
      </>
    );
  }

  return (
    <>
      <RfqSection
        icon={IconBoxMultiple}
        title={t("rfq.detail.sectionTitle") || "詢價明細"}
        bodyClassName="p-0"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              size="xs"
              value={view}
              onChange={(value) => setView(value as "seq" | "group")}
              data={[
                {
                  value: "seq",
                  label: (
                    <span className="flex items-center gap-1">
                      <IconListNumbers size={14} />
                      {t("rfq.detail.viewSeq") || "依項次"}
                    </span>
                  ),
                },
                {
                  value: "group",
                  label: (
                    <span className="flex items-center gap-1">
                      <IconStack2 size={14} />
                      {t("rfq.detail.viewGroup") || "依規格分組"}
                    </span>
                  ),
                },
              ]}
            />
            <Button
              size="xs"
              radius="md"
              color="red"
              variant="light"
              disabled={!editable || selected.size === 0}
              leftSection={<IconTrash size={14} />}
              onClick={deleteSelected}
            >
              {t("rfq.detail.deleteSelected") || "刪除選取"}
              {selected.size > 0 ? ` (${selected.size})` : ""}
            </Button>
            <Button
              size="xs"
              radius="md"
              variant="default"
              disabled={!editable}
              leftSection={<IconPlus size={14} />}
              onClick={() => setAddOpen(true)}
            >
              {t("rfq.detail.addManual") || "手動新增"}
            </Button>
            <Button
              size="xs"
              radius="md"
              color="blue"
              disabled={!editable}
              leftSection={<IconDownload size={14} />}
              onClick={() => setImportOpen(true)}
            >
              {t("rfq.detail.importFromPo") || "從採購單帶入"}
            </Button>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-max min-w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500">
                <Th className="sticky left-0 z-20 w-[76px] bg-gray-50">
                  {t("rfq.detail.colSeq") || "項次"}
                </Th>
                <Th className="sticky left-[76px] z-20 w-[200px] bg-gray-50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                  {t("rfq.detail.colName") || "品名"}
                </Th>
                <Th className="w-[130px]">
                  {t("rfq.detail.colCategory") || "類別"}
                </Th>
                <Th className="w-[150px]">
                  {t("rfq.detail.colSpec") || "規格"}
                </Th>
                <Th className="w-[110px]">
                  {t("rfq.detail.colQty") || "詢價數量"}
                </Th>
                <Th className="w-[90px]">
                  {t("rfq.detail.colUnit") || "單位"}
                </Th>
                <Th className="w-[130px]">
                  {t("rfq.detail.colPriceMode") || "比價模式"}
                </Th>
                {/* <Th className="w-[380px]">
                  {t("rfq.detail.colCategoryFields") || "類別明細"}
                </Th> */}
                <Th className="w-[190px]">
                  {t("rfq.detail.colWeight") || "總重 (kg)"}
                </Th>
                <Th className="w-[140px]">
                  {t("rfq.detail.colDueDate") || "需求交期"}
                </Th>
                <Th className="w-[70px]">{t("rfq.detail.colMtc") || "材證"}</Th>
                {canSeeTarget ? (
                  <Th className="w-[120px]">
                    <span className="inline-flex items-center gap-1">
                      <IconLock size={12} />
                      {t("rfq.detail.colTargetPrice") || "目標單價"}
                    </span>
                  </Th>
                ) : null}
                <Th className="w-[160px]">
                  {t("rfq.detail.colNote") || "備註"}
                </Th>
              </tr>
            </thead>
            <tbody>
              {view === "seq"
                ? lines.map((line) => (
                    <LineRow
                      key={line.id}
                      line={line}
                      selected={selected.has(line.id)}
                      onToggle={() => toggleSelected(line.id)}
                      onUpdate={(patch) => onUpdateLine(line.id, patch)}
                      canSeeTarget={canSeeTarget}
                      editable={editable}
                    />
                  ))
                : (groups ?? []).map(([key, groupLines], groupIndex) => {
                    const qtySum = groupLines.reduce(
                      (sum, line) => sum + line.qty,
                      0,
                    );
                    const weightSum = groupLines.reduce(
                      (sum, line) => sum + (resolveWeight(line, calc)?.kg ?? 0),
                      0,
                    );
                    const tint = groupIndex % 2 === 0;
                    return (
                      <Fragment key={key}>
                        <tr>
                          <td
                            colSpan={columnCount}
                            className={`sticky left-0 border-b border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 ${
                              tint ? "bg-gray-100" : "bg-blue-50"
                            }`}
                          >
                            <span className="inline-flex items-center gap-2">
                              <IconStack2 size={14} className="text-blue-600" />
                              {key}
                              <span className="font-normal text-gray-500">
                                {t("rfq.detail.groupSummary", {
                                  defaultValue:
                                    "・{{n}} 項 ・ 合計數量 {{qty}} ・ 總重 {{weight}} kg",
                                  n: groupLines.length,
                                  qty: qtySum,
                                  weight: fmtWeight(weightSum),
                                })}
                              </span>
                            </span>
                          </td>
                        </tr>
                        {groupLines.map((line) => (
                          <LineRow
                            key={line.id}
                            line={line}
                            selected={selected.has(line.id)}
                            onToggle={() => toggleSelected(line.id)}
                            onUpdate={(patch) => onUpdateLine(line.id, patch)}
                            canSeeTarget={canSeeTarget}
                            editable={editable}
                            tint={tint}
                          />
                        ))}
                      </Fragment>
                    );
                  })}
            </tbody>
          </table>
        </div>
        <p className="border-t border-gray-200 px-6 py-3 text-xs text-gray-500">
          {t("rfq.detail.footerServer", {
            defaultValue:
              "共 {{total}} 筆明細。品名、規格、單位由商品帶出唯讀，數量與交期可調整。",
            total: lines.length,
          })}
        </p>
      </RfqSection>
      {modals}
    </>
  );
}

export default RfqDetailTab;
